import { clsx } from 'clsx'
import {
  ContextMenuSeparator,
  ContextMenuItem,
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
} from './context-menu'
import {
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from './dropdown-menu'
import { ChevronRightIcon } from '@radix-ui/react-icons'

export type MenuItem =
  | {
      key: string
      type: 'action'
      icon: React.ReactNode
      label: string
      disabled?: boolean
      onSelect: (_meta?: Record<string, any>) => void
      onClick?: (event: React.MouseEvent) => void
      intent?: 'danger'
    }
  | {
      key: string
      type: 'submenu'
      icon: React.ReactNode
      label: string
      items: MenuItem[]
      disabled?: boolean
      onClick?: (event: React.MouseEvent) => void
      intent?: 'danger'
    }
  | {
      key: string
      type: 'action-link'
      icon: React.ReactNode
      label: string
      href: string
      disabled?: boolean
      onSelect: (_meta?: Record<string, any>) => void
      onClick?: (event: React.MouseEvent) => void
      intent?: 'danger'
    }
  | { type: 'separator'; key: string }

export const DropdownMenuAction = (actionDef: MenuItem) => {
  switch (actionDef.type) {
    case 'separator':
      return <DropdownMenuSeparator />
    case 'action':
      return (
        <DropdownMenuItem
          className={clsx(
            'space-x-2 text-xs leading-4',
            actionDef.intent === 'danger' && 'text-dark-error'
          )}
          onSelect={actionDef.onSelect}
          onClick={actionDef.onClick}
        >
          {actionDef.icon}
          <span>{actionDef.label}</span>
        </DropdownMenuItem>
      )
    case 'submenu':
      return (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger asChild>
            <span className="space-x-2 text-xs leading-4">
              {actionDef.icon}
              <span>{actionDef.label}</span>
              <ChevronRightIcon
                width={12}
                height={12}
                className="text-dark-muted !ml-auto"
              />
            </span>
          </DropdownMenuSubTrigger>

          <DropdownMenuSubContent>
            {actionDef.items.map((sub) => {
              if (sub.type === 'separator') {
                return <DropdownMenuSeparator key={sub.key} />
              }

              if (sub.type !== 'action') {
                throw new Error('Invalid action type')
              }

              return (
                <DropdownMenuItem
                  key={sub.key}
                  className={clsx(
                    'space-x-2 text-xs leading-4',
                    sub.intent === 'danger' && 'text-dark-error'
                  )}
                  onSelect={sub.onSelect}
                  onClick={sub.onClick}
                >
                  {sub.icon}
                  <span>{sub.label}</span>
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )
    default:
      throw new Error('Invalid action type')
  }
}

export const SimpleDropdownMenu = ({
  children,
  actions,
  contentProps,
}: {
  children?: React.ReactNode
  actions: MenuItem[]
  contentProps?: React.ComponentProps<typeof DropdownMenuContent>
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent sideOffset={6} align="end" {...contentProps}>
        {actions.map((action) => (
          <DropdownMenuAction {...action} key={action.key} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const ContextMenuAction = (actionDef: MenuItem) => {
  switch (actionDef.type) {
    case 'separator':
      return <ContextMenuSeparator />
    case 'action':
      return (
        <ContextMenuItem
          className={clsx(
            'space-x-2 text-xs leading-4',
            actionDef.intent === 'danger' && 'text-dark-error'
          )}
          onSelect={actionDef.onSelect}
          onClick={actionDef.onClick}
        >
          {actionDef.icon}
          <span>{actionDef.label}</span>
        </ContextMenuItem>
      )
    default:
      throw new Error('Invalid action type')
  }
}

export const SimpleContextMenu = ({
  children,
  actions,
  controls,
}: {
  children?: React.ReactNode
  actions: MenuItem[]
  controls?: { open: boolean; onOpenChange: (v: boolean) => void }
}) => {
  return (
    <ContextMenu {...controls}>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        {actions.map((action) => (
          <ContextMenuAction {...action} key={action.key} />
        ))}
      </ContextMenuContent>
    </ContextMenu>
  )
}
